import type { GuideStepContent } from './phantomContent';

export const BACKPACK_CONTENT = {
  title: 'Backpack wallet guide',
  lead:
    'Backpack is a Solana-ready browser wallet (and more). Follow these steps to install it, connect to Canadawale Yar, and send or receive CWY.',
  steps: [
    {
      title: 'Open Backpack',
      phase: 'install',
      body: [
        'You may land on backpack.exchange (trading) or backpack.app (wallet & downloads).',
        'For the browser extension used with CWY, continue to backpack.app and use Download / Chrome Extension.',
      ],
      link: {
        href: 'https://backpack.app',
        label: 'Open backpack.app',
      },
      image: {
        src: '/wallet_ui/backpack-exchange.jpg',
        alt: 'Backpack exchange homepage at backpack.exchange',
        caption: 'Backpack also runs an exchange site. For the CWY browser wallet, use backpack.app to download the extension.',
      },
    },
    {
      title: 'Download the Chrome extension',
      phase: 'install',
      body: [
        'On backpack.app, choose Chrome Extension (iOS and Google Play are also available for mobile).',
        'Always download from the official Backpack site or Chrome Web Store — not from random ads.',
      ],
      image: {
        src: '/wallet_ui/backpack-download.jpg',
        alt: 'Backpack.app download page with iOS, Google Play, and Chrome Extension buttons',
        caption: 'On backpack.app, click Chrome Extension to install the desktop wallet.',
      },
      link: {
        href: 'https://backpack.app',
        label: 'Open backpack.app',
      },
    },
    {
      title: 'Add Backpack from the Chrome Web Store',
      phase: 'install',
      body: [
        'On the Backpack store page, click Add to Chrome (or Add to Brave / Edge if that is your browser).',
        'Confirm Add extension, then pin Backpack in your toolbar.',
      ],
      image: {
        src: '/wallet_ui/backpack-chrome-store.jpg',
        alt: 'Chrome Web Store page for Backpack with Add to Chrome button',
        caption: 'Click Add to Chrome, then confirm to install the Backpack extension.',
      },
      tips: [
        'Create or import a wallet and save your recovery phrase offline. Never share it with anyone.',
      ],
    },
    {
      title: 'Select Backpack on CWY',
      phase: 'connect',
      body: [
        'Sign in to Canadawale Yar and open Profile → Wallet (My Crypto Wallet).',
        'Click Connect wallet (or open the wallet menu), then choose Backpack and click Switch / Connect.',
      ],
      image: {
        src: '/wallet_ui/backpack-change-wallet.jpg',
        alt: 'CWY Change wallet panel listing Phantom, Solflare, Backpack, Brave Wallet, and Nightly',
        caption: 'Select Backpack from the Change wallet list to link it to your CWY account.',
      },
      tips: [
        'If Backpack does not appear, make sure the extension is installed and unlocked, then refresh the page.',
      ],
    },
    {
      title: 'Approve the signature request',
      phase: 'connect',
      body: [
        'Backpack will show a message signing request from Canadawale Yar (app.canadawaleyar.com).',
        'This only proves you own the wallet — it does not send money. Review the message, then click Approve.',
      ],
      image: {
        src: '/wallet_ui/backpack-signature-request.jpg',
        alt: 'Backpack message signing request verifying CWY wallet ownership for provider backpack',
        caption: 'Click Approve on the Backpack signature request to finish connecting.',
      },
      warnings: [
        'Check that the site is app.canadawaleyar.com before you approve.',
        'Leave Auto-approve message signing off unless you understand the risk.',
      ],
    },
    {
      title: 'Receive CWY',
      phase: 'receive',
      body: [
        'Once Backpack is connected, click Receive on the Wallet page (Request CWY tokens).',
        'Confirm your receiving address shows via Backpack, choose the amount (minimum 1 CWY), then click Request CWY.',
        'Depending on program rules, a request may need approval before CWY is sent to your Backpack wallet.',
      ],
      image: {
        src: '/wallet_ui/backpack-receive-cwy.jpg',
        alt: 'Receive CWY modal showing Backpack wallet address and Request CWY button',
        caption: 'Use Receive to request CWY into your connected Backpack wallet.',
      },
    },
    {
      title: 'Send CWY back to the vault',
      phase: 'send',
      body: [
        'To return CWY to the platform, click Send (Return CWY tokens / Add CWY to Your Account).',
        'Choose how much to deposit, confirm the verified CWY destination address, then click Deposit to CWY and approve in Backpack when prompted.',
      ],
      image: {
        src: '/wallet_ui/backpack-deposit-cwy.jpg',
        alt: 'Add CWY to Your Account modal with Backpack wallet and verified CWY destination address',
        caption: 'Deposit CWY from Backpack to the verified CWY vault. You need CWY balance (and a little SOL for fees) before Deposit enables.',
      },
      warnings: [
        'If Deposit is disabled, check that you have enough CWY available and a small amount of SOL for Solana network fees.',
      ],
    },
  ] satisfies GuideStepContent[],
} as const;
